<script setup lang="ts">
import '@fortawesome/fontawesome-free/css/all.min.css'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { normalizeFieldBool, normalizeFieldText } from '~/composables/useCustomFields'
import { STORAGE_KEYS } from '~/utils/storage'

const siteData = await useSiteData()
const route = useRoute()
const navigationEpoch = useNavigationEpoch()
const fields = siteData?.setting?.customFields

const rawDarkColorScheme = normalizeFieldText(fields, 'darkColorScheme', 'yuelan').toLowerCase()
const darkColorScheme = rawDarkColorScheme === 'midnight' ? 'midnight' : 'yuelan'
const rawThemeColor = normalizeFieldText(fields, 'themeColor', '#1F6FEB').toUpperCase()
const themeColor = /^#[0-9A-F]{6}$/.test(rawThemeColor) ? rawThemeColor : '#1F6FEB'
const serifFontEnabled = normalizeFieldBool(fields, 'serifFontEnabled', true)
const googleFontEnabled = normalizeFieldBool(fields, 'googleFontEnabled', true)
const googleFontFamily = serifFontEnabled ? 'Noto+Serif+SC' : 'Noto+Sans+SC'
const siteName = normalizeFieldText(fields, 'siteName')
const siteDescription = normalizeFieldText(fields, 'siteDescription', normalizeFieldText(fields, 'seoDescription', ''))
const seoKeywords = normalizeFieldText(fields, 'seoKeywords', '')
const seoImage = normalizeFieldText(fields, 'seoImage', '')
const siteFavicon = normalizeFieldText(fields, 'siteFavicon', '')
const umamiEnabled = normalizeFieldBool(fields, 'umamiEnabled', false)
const umamiHost = normalizeFieldText(fields, 'umamiHost', '')
const umamiWebsiteId = normalizeFieldText(fields, 'umamiWebsiteId', '')
const customCss = normalizeFieldText(fields, 'customCss', '')
const customJs = normalizeFieldText(fields, 'customJs', '')
const isCustomThemeColor = themeColor !== '#1F6FEB'

const umamiScriptSrc = computed(() => {
  if (!umamiHost) {
    return ''
  }
  if (umamiHost.endsWith('.js')) {
    return umamiHost
  }
  return `${umamiHost.replace(/\/+$/, '')}/script.js`
})

const faviconHref = siteFavicon ? resolveImageAssetUrl(siteFavicon) : ''
const safeCustomJs = customJs.replace(/<\/script/gi, '<\\/script')

watch(
  () => `${route.path}?${JSON.stringify(route.query || {})}`,
  () => {
    navigationEpoch.value += 1
  },
  { immediate: true }
)

useSeoMeta({
  titleTemplate: titleChunk => (titleChunk ? `${titleChunk}` : siteName),
  description: siteDescription || undefined,
  keywords: seoKeywords || undefined,
  ogSiteName: siteName,
  ogDescription: siteDescription || undefined,
  ogImage: seoImage ? resolveImageAssetUrl(seoImage) : undefined,
  twitterCard: seoImage ? 'summary_large_image' : undefined,
  twitterImage: seoImage ? resolveImageAssetUrl(seoImage) : undefined
})

useHead(() => {
  const styles: Array<{
    key: string
    type: 'text/css'
    children: string
    tagPosition?: 'head' | 'bodyOpen' | 'bodyClose'
  }> = []
  const scripts: Array<
    | {
        defer: true
        src: string
        'data-website-id': string
      }
    | {
        key: string
        type: 'text/javascript'
        children: string
        tagPosition: 'head' | 'bodyClose'
        tagPriority?: 'critical'
      }
  > = []
  // 在首屏绘制前读取侧栏状态，避免挂载后才播放收起动画
  scripts.push({
    key: 'sidebar-initial-state',
    type: 'text/javascript',
    tagPosition: 'head',
    tagPriority: 'critical',
    children: `try{var sidebarState=localStorage.getItem(${JSON.stringify(STORAGE_KEYS.DETAIL_SIDEBAR_COLLAPSED)});if(sidebarState==='0'||sidebarState==='1'){document.documentElement.dataset.sidebarCollapsed=sidebarState}}catch{}`
  })
  // 在首屏绘制前应用相册布局，挂载后交接给页面状态
  scripts.push({
    key: 'album-initial-state',
    type: 'text/javascript',
    tagPosition: 'head',
    tagPriority: 'critical',
    children: `try{[['albumListLayout',${JSON.stringify(STORAGE_KEYS.ALBUM_LIST_LAYOUT)}],['albumDetailLayout',${JSON.stringify(STORAGE_KEYS.ALBUM_DETAIL_LAYOUT)}]].forEach(function(item){var layout=localStorage.getItem(item[1]);if(['relaxed','immersive'].includes(layout)){document.documentElement.dataset[item[0]]=layout}})}catch{}`
  })
  const links = googleFontEnabled
    ? [
        { key: 'google-fonts-api', rel: 'preconnect' as const, href: 'https://fonts.googleapis.com' },
        { key: 'google-fonts-static', rel: 'preconnect' as const, href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' as const },
        {
          key: 'google-font',
          rel: 'stylesheet' as const,
          href: `https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=${googleFontFamily}:wght@400;500;600;700&display=swap`
        }
      ]
    : []
  if (umamiEnabled && umamiWebsiteId && umamiScriptSrc.value) {
    scripts.push({ defer: true, src: umamiScriptSrc.value, 'data-website-id': umamiWebsiteId })
  }
  if (safeCustomJs) {
    scripts.push({ key: 'theme-custom-js', type: 'text/javascript', children: safeCustomJs, tagPosition: 'bodyClose' })
  }
  if (isCustomThemeColor) {
    styles.push({
      key: 'theme-color',
      type: 'text/css',
      children: `:root{--theme-brand-light:${themeColor};--theme-brand-light-soft:color-mix(in srgb,${themeColor} 10%,#fff);--theme-brand-dark:color-mix(in srgb,${themeColor} 75%,#fff);--theme-brand-dark-soft:color-mix(in srgb,${themeColor} 20%,var(--bg-soft))}`
    })
  }
  if (customCss) {
    styles.push({
      key: 'theme-custom-css',
      type: 'text/css',
      children: customCss,
      tagPosition: 'bodyClose'
    })
  }

  return {
    htmlAttrs: {
      'data-dark-color-scheme': darkColorScheme,
      'data-serif-font': serifFontEnabled ? 'true' : 'false'
    },
    link: [
      ...links,
      ...(faviconHref
        ? [
            { rel: 'icon' as const, href: faviconHref },
            { rel: 'shortcut icon' as const, href: faviconHref }
          ]
        : [])
    ],
    style: styles,
    script: scripts
  }
})
</script>

<template>
  <BlogSvgSymbols />
  <NuxtLoadingIndicator :color="themeColor" :height="3" />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>
